import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  ChevronRight,
  Target, 
  AlertTriangle, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  Zap,
  Shield,
  Activity,
  ArrowRight,
  Maximize2,
  X,
  Download
} from 'lucide-react';

const PrognosisFlowchart = ({ prognosisFlow }) => {
  const [activeScenario, setActiveScenario] = useState('most_likely');
  const [expandedStage, setExpandedStage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [animatedNodes, setAnimatedNodes] = useState([]);
  const canvasRef = useRef(null);

  // Animate nodes on mount
  useEffect(() => {
    if (prognosisFlow?.progression_path) {
      const timer = setTimeout(() => {
        prognosisFlow.progression_path.forEach((_, idx) => {
          setTimeout(() => {
            setAnimatedNodes(prev => [...prev, idx]);
          }, idx * 300);
        });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [prognosisFlow]);

  const openModal = useCallback((content, title) => {
    setModalContent({ content, title });
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalContent(null);
  }, []);

  // Download chart as image
  const downloadChart = useCallback(async () => {
    if (canvasRef.current) {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(canvasRef.current, {
          backgroundColor: '#ffffff',
          scale: 2
        });
        const link = document.createElement('a');
        link.download = 'prognosis-flowchart.png';
        link.href = canvas.toDataURL();
        link.click();
      } catch (error) {
        console.error('Download error:', error);
        alert('Download feature: Right-click on the flowchart and select "Save image as..."');
      }
    }
  }, []);

  if (!prognosisFlow) return null;

  const { 
    current_state, 
    scenarios, 
    milestones, 
    progression_path, 
    critical_decision_points, 
    preventive_measures 
  } = prognosisFlow;

  const scenarioConfig = {
    best_case: { 
      color: '#10b981', 
      bgColor: '#ecfdf5', 
      borderColor: '#34d399',
      icon: Target, 
      label: 'Best Case',
      emoji: '🎯'
    },
    most_likely: { 
      color: '#3b82f6', 
      bgColor: '#eff6ff', 
      borderColor: '#60a5fa',
      icon: TrendingUp, 
      label: 'Most Likely',
      emoji: '📊'
    },
    worst_case: { 
      color: '#ef4444', 
      bgColor: '#fef2f2', 
      borderColor: '#f87171',
      icon: AlertTriangle, 
      label: 'Worst Case',
      emoji: '⚠️'
    }
  };

  const stageColors = [
    { main: '#10b981', light: '#d1fae5', dark: '#059669' },  // Green - Start
    { main: '#3b82f6', light: '#dbeafe', dark: '#2563eb' },  // Blue - Short term
    { main: '#8b5cf6', light: '#ede9fe', dark: '#7c3aed' },  // Purple - Medium term
    { main: '#f59e0b', light: '#fef3c7', dark: '#d97706' },  // Amber - Long term
    { main: '#ec4899', light: '#fce7f3', dark: '#db2777' },  // Pink - Extended
  ];

  const getStageColor = (idx, total) => {
    const colorIndex = Math.min(idx, stageColors.length - 1);
    return stageColors[colorIndex];
  };

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '16px', 
      padding: '2rem', 
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h3 style={{ 
            color: '#0f172a', 
            fontSize: '1.5rem', 
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Activity size={28} style={{ color: '#3b82f6' }} />
            Interactive Prognosis Flowchart
          </h3>
          <p style={{ color: '#64748b', margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
            Explore your health journey and possible outcomes
          </p>
        </div>
        <button
          onClick={downloadChart}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.9rem',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
          }}
        >
          <Download size={18} />
          Download Chart
        </button>
      </div>

      {/* Current State Banner */}
      {current_state && (
        <div style={{
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid #bae6fd',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Info size={24} color="white" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#0369a1', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
              Current Health Status
            </div>
            <p style={{ color: '#0c4a6e', margin: 0, lineHeight: 1.6 }}>
              {current_state.description}
            </p>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <div style={{ 
                padding: '0.5rem 1rem', 
                background: getSeverityColor(current_state.severity).bg,
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: getSeverityColor(current_state.severity).text
              }}>
                Severity: {current_state.severity || 'Moderate'}
              </div>
              <div style={{ 
                padding: '0.5rem 1rem', 
                background: '#f1f5f9',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Clock size={14} />
                {current_state.timepoint || 'Current'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Selector */}
      {scenarios && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            color: '#1e293b', 
            fontSize: '1.1rem', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Zap size={20} style={{ color: '#f59e0b' }} />
            Possible Outcomes
          </h4>
          
          {/* Scenario Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            marginBottom: '1.5rem',
            background: '#f1f5f9',
            padding: '0.5rem',
            borderRadius: '12px',
            flexWrap: 'wrap'
          }}>
            {Object.entries(scenarioConfig).map(([key, config]) => {
              const scenario = scenarios[key];
              if (!scenario) return null;
              const isActive = activeScenario === key;
              const Icon = config.icon;
              
              return (
                <button
                  key={key}
                  onClick={() => setActiveScenario(key)}
                  style={{
                    flex: '1 1 auto',
                    minWidth: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    background: isActive ? config.bgColor : 'transparent',
                    color: isActive ? config.color : '#64748b',
                    border: isActive ? `2px solid ${config.borderColor}` : '2px solid transparent',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  <Icon size={18} />
                  {config.label}
                </button>
              );
            })}
          </div>

          {/* Active Scenario Card */}
          {scenarios[activeScenario] && (
            <div style={{
              background: scenarioConfig[activeScenario].bgColor,
              borderRadius: '16px',
              padding: '2rem',
              border: `2px solid ${scenarioConfig[activeScenario].borderColor}`,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background decoration */}
              <div style={{
                position: 'absolute',
                top: '-30px',
                right: '-30px',
                width: '120px',
                height: '120px',
                background: scenarioConfig[activeScenario].borderColor,
                opacity: 0.1,
                borderRadius: '50%'
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <span style={{ fontSize: '2rem' }}>{scenarioConfig[activeScenario].emoji}</span>
                  <h5 style={{ 
                    color: scenarioConfig[activeScenario].color, 
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: 700
                  }}>
                    {scenarioConfig[activeScenario].label} Scenario
                  </h5>
                </div>
                
                <p style={{ 
                  color: '#475569', 
                  fontSize: '0.95rem', 
                  lineHeight: 1.6,
                  marginBottom: '1.5rem',
                  fontStyle: 'italic'
                }}>
                  {scenarios[activeScenario].description}
                </p>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '1rem' 
                }}>
                  <div style={{
                    padding: '1.25rem',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: scenarioConfig[activeScenario].color,
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Expected Outcome
                    </div>
                    <div style={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {scenarios[activeScenario].outcome}
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '1.25rem',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: scenarioConfig[activeScenario].color,
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Clock size={14} />
                      Timeline
                    </div>
                    <div style={{ color: '#334155', fontSize: '0.95rem' }}>
                      {scenarios[activeScenario].timeline}
                    </div>
                  </div>

                  {scenarios[activeScenario].probability && (
                    <div style={{
                      padding: '1.25rem',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ 
                        fontWeight: 600, 
                        color: scenarioConfig[activeScenario].color,
                        marginBottom: '0.5rem',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Probability
                      </div>
                      <div style={{ 
                        color: '#334155', 
                        fontSize: '1.5rem',
                        fontWeight: 700
                      }}>
                        {scenarios[activeScenario].probability}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Interactive Progression Timeline */}
      {progression_path && progression_path.length > 0 && (
        <div ref={canvasRef} style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            color: '#1e293b', 
            fontSize: '1.1rem', 
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <TrendingUp size={20} style={{ color: '#10b981' }} />
            Health Journey Timeline
          </h4>

          {/* SVG-based Flowchart */}
          <div style={{
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '16px',
            padding: '2rem',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Connection Lines SVG */}
            <svg 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#f59e0b" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
            </svg>

            {/* Timeline Nodes */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0'
            }}>
              {progression_path.map((stage, idx) => {
                const colors = getStageColor(idx, progression_path.length);
                const isExpanded = expandedStage === idx;
                const isAnimated = animatedNodes.includes(idx);
                const isLast = idx === progression_path.length - 1;

                return (
                  <div key={idx} style={{ position: 'relative' }}>
                    {/* Connector Line */}
                    {!isLast && (
                      <div style={{
                        position: 'absolute',
                        left: '24px',
                        top: '48px',
                        bottom: '-24px',
                        width: '4px',
                        background: `linear-gradient(180deg, ${colors.main} 0%, ${getStageColor(idx + 1, progression_path.length).main} 100%)`,
                        borderRadius: '2px',
                        zIndex: 0
                      }} />
                    )}

                    <div 
                      style={{
                        display: 'flex',
                        gap: '1.5rem',
                        alignItems: 'flex-start',
                        paddingBottom: isLast ? 0 : '2rem',
                        opacity: isAnimated ? 1 : 0,
                        transform: isAnimated ? 'translateX(0)' : 'translateX(-20px)',
                        transition: 'all 0.5s ease-out'
                      }}
                    >
                      {/* Node Circle */}
                      <div
                        onClick={() => setExpandedStage(isExpanded ? null : idx)}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          cursor: 'pointer',
                          flexShrink: 0,
                          boxShadow: `0 4px 12px ${colors.main}40`,
                          border: '3px solid white',
                          zIndex: 1,
                          transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.15)';
                          e.currentTarget.style.boxShadow = `0 6px 20px ${colors.main}60`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = `0 4px 12px ${colors.main}40`;
                        }}
                      >
                        {idx + 1}
                      </div>

                      {/* Content Card */}
                      <div
                        onClick={() => setExpandedStage(isExpanded ? null : idx)}
                        style={{
                          flex: 1,
                          background: 'white',
                          borderRadius: '16px',
                          padding: '1.5rem',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                          border: `2px solid ${isExpanded ? colors.main : '#e2e8f0'}`,
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.borderColor = colors.light;
                            e.currentTarget.style.transform = 'translateX(5px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }
                        }}
                      >
                        {/* Card Header */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '0.75rem'
                        }}>
                          <div>
                            <div style={{ 
                              fontWeight: 700, 
                              color: colors.dark,
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem'
                            }}>
                              {stage.stage}
                            </div>
                            <div style={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.25rem 0.75rem',
                              background: colors.light,
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: colors.dark
                            }}>
                              <Clock size={12} />
                              {stage.timeframe}
                            </div>
                          </div>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            background: colors.light,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.3s'
                          }}>
                            {isExpanded ? (
                              <ChevronUp size={16} style={{ color: colors.main }} />
                            ) : (
                              <ChevronDown size={16} style={{ color: colors.main }} />
                            )}
                          </div>
                        </div>

                        <p style={{ 
                          color: '#475569', 
                          margin: 0,
                          lineHeight: 1.6,
                          fontSize: '0.95rem'
                        }}>
                          {stage.description}
                        </p>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <div style={{
                            marginTop: '1.5rem',
                            paddingTop: '1.5rem',
                            borderTop: `2px dashed ${colors.light}`,
                            animation: 'fadeIn 0.3s ease-out'
                          }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                              gap: '1rem' 
                            }}>
                              {stage.interventions && stage.interventions.length > 0 && (
                                <div style={{
                                  padding: '1rem',
                                  background: '#fef3c7',
                                  borderRadius: '10px'
                                }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    color: '#92400e',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.85rem'
                                  }}>
                                    <Zap size={14} />
                                    Interventions
                                  </div>
                                  <ul style={{ 
                                    margin: 0, 
                                    paddingLeft: '1.25rem',
                                    color: '#78350f',
                                    fontSize: '0.85rem'
                                  }}>
                                    {stage.interventions.map((int, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{int}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {stage.next_steps && stage.next_steps.length > 0 && (
                                <div style={{
                                  padding: '1rem',
                                  background: '#dbeafe',
                                  borderRadius: '10px'
                                }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    color: '#1e40af',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.85rem'
                                  }}>
                                    <ArrowRight size={14} />
                                    Next Steps
                                  </div>
                                  <ul style={{ 
                                    margin: 0, 
                                    paddingLeft: '1.25rem',
                                    color: '#1e3a8a',
                                    fontSize: '0.85rem'
                                  }}>
                                    {stage.next_steps.map((step, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{step}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {stage.success_indicators && stage.success_indicators.length > 0 && (
                                <div style={{
                                  padding: '1rem',
                                  background: '#d1fae5',
                                  borderRadius: '10px'
                                }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    color: '#065f46',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.85rem'
                                  }}>
                                    <CheckCircle size={14} />
                                    Success Indicators
                                  </div>
                                  <ul style={{ 
                                    margin: 0, 
                                    paddingLeft: '1.25rem',
                                    color: '#064e3b',
                                    fontSize: '0.85rem'
                                  }}>
                                    {stage.success_indicators.map((ind, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{ind}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {stage.goals && stage.goals.length > 0 && (
                                <div style={{
                                  padding: '1rem',
                                  background: '#ede9fe',
                                  borderRadius: '10px'
                                }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    color: '#5b21b6',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.85rem'
                                  }}>
                                    <Target size={14} />
                                    Goals
                                  </div>
                                  <ul style={{ 
                                    margin: 0, 
                                    paddingLeft: '1.25rem',
                                    color: '#4c1d95',
                                    fontSize: '0.85rem'
                                  }}>
                                    {stage.goals.map((goal, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{goal}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {stage.sustainability && stage.sustainability.length > 0 && (
                                <div style={{
                                  padding: '1rem',
                                  background: '#fce7f3',
                                  borderRadius: '10px'
                                }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    color: '#9d174d',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.85rem'
                                  }}>
                                    <Shield size={14} />
                                    Long-term Habits
                                  </div>
                                  <ul style={{ 
                                    margin: 0, 
                                    paddingLeft: '1.25rem',
                                    color: '#831843',
                                    fontSize: '0.85rem'
                                  }}>
                                    {stage.sustainability.map((item, i) => (
                                      <li key={i} style={{ marginBottom: '0.25rem' }}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>

                            {/* Expand to full view button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal(stage, `Stage ${idx + 1}: ${stage.stage}`);
                              }}
                              style={{
                                marginTop: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                background: colors.light,
                                color: colors.dark,
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 600
                              }}
                            >
                              <Maximize2 size={14} />
                              View Full Details
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Milestones Section */}
      {milestones && milestones.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            color: '#1e293b', 
            fontSize: '1.1rem', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Target size={20} style={{ color: '#8b5cf6' }} />
            Key Milestones
          </h4>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {milestones.map((milestone, idx) => (
              <div 
                key={idx}
                style={{
                  background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #e9d5ff',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem'
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    background: '#8b5cf6',
                    borderRadius: '20px',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {milestone.timepoint}
                  </div>
                </div>
                
                <div style={{ fontWeight: 600, color: '#5b21b6', marginBottom: '0.5rem' }}>
                  {milestone.event}
                </div>
                
                {milestone.expected_changes && (
                  <p style={{ 
                    color: '#6b21a8', 
                    fontSize: '0.9rem', 
                    margin: '0 0 1rem 0',
                    lineHeight: 1.5
                  }}>
                    {milestone.expected_changes}
                  </p>
                )}

                {milestone.interventions_needed && milestone.interventions_needed.length > 0 && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 600, 
                      color: '#7c3aed',
                      marginBottom: '0.25rem'
                    }}>
                      Actions Needed:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {milestone.interventions_needed.map((action, i) => (
                        <span 
                          key={i}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: 'white',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            color: '#6b21a8'
                          }}
                        >
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {milestone.warning_signs && milestone.warning_signs.length > 0 && (
                  <div style={{
                    padding: '0.75rem',
                    background: '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 600, 
                      color: '#dc2626',
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <AlertTriangle size={12} />
                      Watch For:
                    </div>
                    <ul style={{ 
                      margin: 0, 
                      paddingLeft: '1rem', 
                      fontSize: '0.8rem',
                      color: '#b91c1c'
                    }}>
                      {milestone.warning_signs.map((sign, i) => (
                        <li key={i}>{sign}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Critical Decision Points */}
      {critical_decision_points && critical_decision_points.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            color: '#1e293b', 
            fontSize: '1.1rem', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
            Critical Decision Points
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {critical_decision_points.map((point, idx) => (
              <div 
                key={idx}
                style={{
                  background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '2px solid #fcd34d',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)'
                }} />
                
                <div style={{ marginLeft: '0.5rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    marginBottom: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: '#f59e0b',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Clock size={14} />
                      {point.timepoint}
                    </div>
                    <div style={{ fontWeight: 700, color: '#92400e', fontSize: '1.05rem' }}>
                      {point.decision}
                    </div>
                  </div>

                  {point.options && point.options.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ 
                        fontWeight: 600, 
                        color: '#b45309', 
                        marginBottom: '0.5rem',
                        fontSize: '0.9rem'
                      }}>
                        Available Options:
                      </div>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '0.75rem' 
                      }}>
                        {point.options.map((option, oidx) => (
                          <div 
                            key={oidx}
                            style={{
                              padding: '0.75rem 1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #fcd34d',
                              color: '#78350f',
                              fontSize: '0.9rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <ChevronRight size={16} style={{ color: '#f59e0b' }} />
                            {option}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {point.recommendation && (
                    <div style={{
                      padding: '1rem',
                      background: 'white',
                      borderRadius: '10px',
                      border: '2px solid #34d399',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <CheckCircle size={18} color="white" />
                      </div>
                      <div>
                        <div style={{ 
                          fontWeight: 600, 
                          color: '#059669', 
                          marginBottom: '0.25rem',
                          fontSize: '0.9rem'
                        }}>
                          Recommended Approach
                        </div>
                        <div style={{ color: '#047857', fontSize: '0.95rem', lineHeight: 1.5 }}>
                          {point.recommendation}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preventive Measures */}
      {preventive_measures && preventive_measures.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          borderRadius: '16px',
          padding: '2rem',
          border: '2px solid #6ee7b7'
        }}>
          <h4 style={{ 
            color: '#065f46', 
            fontSize: '1.1rem', 
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Shield size={20} style={{ color: '#10b981' }} />
            Preventive Measures to Improve Prognosis
          </h4>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            {preventive_measures.map((measure, idx) => (
              <div 
                key={idx}
                style={{
                  padding: '1rem 1.25rem',
                  background: 'white',
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(5px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <CheckCircle size={14} color="white" />
                </div>
                <span style={{ color: '#047857', fontSize: '0.95rem', lineHeight: 1.4 }}>
                  {measure}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal for full details */}
      {showModal && modalContent && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={closeModal}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem' }}>
                {modalContent.title}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#f1f5f9',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ color: '#475569', lineHeight: 1.7 }}>
              <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
                {modalContent.content.description}
              </p>
              
              {modalContent.content.timeframe && (
                <div style={{ 
                  padding: '0.75rem 1rem',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Clock size={16} style={{ color: '#3b82f6' }} />
                  <strong>Timeframe:</strong> {modalContent.content.timeframe}
                </div>
              )}
              
              {/* Display all available arrays in the modal */}
              {['interventions', 'next_steps', 'success_indicators', 'goals', 'sustainability'].map(key => (
                modalContent.content[key] && modalContent.content[key].length > 0 && (
                  <div key={key} style={{ marginBottom: '1rem' }}>
                    <h4 style={{ 
                      color: '#1e293b', 
                      fontSize: '0.95rem',
                      marginBottom: '0.5rem',
                      textTransform: 'capitalize'
                    }}>
                      {key.replace(/_/g, ' ')}:
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                      {modalContent.content[key].map((item, i) => (
                        <li key={i} style={{ marginBottom: '0.25rem' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Helper function for severity colors
const getSeverityColor = (severity) => {
  switch (severity?.toLowerCase()) {
    case 'mild':
      return { bg: '#d1fae5', text: '#065f46' };
    case 'moderate':
      return { bg: '#fef3c7', text: '#92400e' };
    case 'severe':
      return { bg: '#fee2e2', text: '#991b1b' };
    default:
      return { bg: '#f1f5f9', text: '#475569' };
  }
};

export default PrognosisFlowchart;
